import React, { useState, useEffect } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { supabase, UserRole } from '../../lib/supabase';
import { getRoleBasePath, checkStudentRestriction } from '../../context/AuthContext';
import { Card, Button, Input, Modal } from '../ui';
import { clsx } from 'clsx';
import { setAccountSwitcherBusy } from '../../lib/accountSwitcherState';

// ─── Types ────────────────────────────────────────────────────
export type SavedAccount = {
  username: string;
  password: string;        // stored locally only, never logged
  school_id: string;
  school_name: string;
  user_full_name: string;
  role: string;
  last_used: string;       // ISO timestamp
};

const STORAGE_KEY = 'edtrack_saved_accounts';

// ─── Identity normalization ────────────────────────────────────
// Two different places in the app save accounts to this same storage:
// this component's own "Add Account" flow (keys by whatever the user
// typed) and AuthContext's normal sign-in flow (previously keyed by the
// DB's stored `users.username` field). If those two ever disagree on
// casing/whitespace for the same person, they silently create two
// separate saved entries for one real account instead of one. All
// reads/writes below go through this single normalizer so both flows
// converge on the same key.
function normalizeKey(s: string): string {
  return (s || '').trim().toLowerCase();
}

/** The identifier that actually authenticates this user — phone for
 * parents (parents log in by phone; users.username for a parent is a
 * separate generated string, not their phone), username otherwise.
 * Layouts should pass this (not the raw `user.username` field) as
 * `currentUsername` so the active account reliably matches its own
 * saved-account entry regardless of role. */
export function getAccountKey(user: { username?: string | null; phone?: string | null; role?: string | null } | null | undefined): string {
  if (!user) return '';
  const raw = user.role === 'parent' ? user.phone : user.username;
  return raw || '';
}

// ─── Validation client ─────────────────────────────────────────
// IMPORTANT: this is a SEPARATE client instance from the main `supabase`
// export, with persistSession/autoRefreshToken OFF. It exists purely to
// check credentials + look up profile/school info while adding a new
// saved account, AND (see handleSwitch below) to verify a switch target's
// credentials + restriction status before ever touching the real session.
// Using the main `supabase` client for either of these would immediately
// overwrite the currently logged-in user's session the moment
// signInWithPassword() is called — even if the check then fails a step
// later (e.g. "Account not found", or the target turns out restricted) —
// silently switching who the app is authenticated as while the UI still
// shows the old user's data. Only the final, confirmed-safe step of
// handleSwitch (followed by a full page reload) should ever touch the
// real `supabase` client's session.
//
// storageKey is set here even though persistSession is off, because
// Supabase's auth client uses the storage key (not just persistSession)
// to name the BroadcastChannel it uses to sync auth state across every
// client instance pointed at the same project. Without a distinct key
// here, this client shared a channel with the main `supabase` client —
// so every sign-in/sign-out this client did while checking credentials
// could still emit auth-state events the main client picked up, corrupting
// the real session's refresh token mid-use (this was the actual cause of
// the "Multiple GoTrueClient instances" warning and the resulting
// intermittent session/RLS failures elsewhere in the app).
const validationClient = createClient(
  process.env.REACT_APP_SUPABASE_URL as string,
  process.env.REACT_APP_SUPABASE_ANON_KEY as string,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'edtrack-validation-client-unused',
    },
  }
);

// ─── Local storage helpers ────────────────────────────────────
export function getSavedAccounts(): SavedAccount[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: SavedAccount[] = raw ? JSON.parse(raw) : [];

    // Self-healing dedupe: earlier versions of this code saved the same
    // account under two different keys (typed identifier vs. DB
    // username field), which could leave real duplicate rows sitting in
    // someone's localStorage from before this fix. Merge those here —
    // group by normalized identity, keep whichever copy was used most
    // recently — so existing installs clean themselves up automatically
    // instead of requiring anyone to clear storage by hand.
    const byKey = new Map<string, SavedAccount>();
    for (const acc of parsed) {
      const key = normalizeKey(acc.username);
      const prev = byKey.get(key);
      if (!prev || new Date(acc.last_used).getTime() > new Date(prev.last_used).getTime()) {
        byKey.set(key, acc);
      }
    }
    const deduped = Array.from(byKey.values());
    if (deduped.length !== parsed.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(deduped));
    }
    return deduped;
  } catch {
    return [];
  }
}

export function saveAccount(account: SavedAccount) {
  const existing = getSavedAccounts();
  const normalized: SavedAccount = { ...account, username: normalizeKey(account.username) };
  const idx = existing.findIndex(a => normalizeKey(a.username) === normalized.username);
  if (idx >= 0) {
    // Some call sites (portal-mount "keep this account fresh in the
    // switcher" effects in PrincipalLayout/StudentPages/TeacherLayout)
    // save with password: '' just to register presence/last_used —
    // that was silently overwriting the real password saved by Add
    // Account or a normal login, so every subsequent switch attempt
    // failed with invalid_credentials even though nothing had actually
    // changed. A blank incoming password never clobbers a known one.
    const preservedPassword = normalized.password ? normalized.password : existing[idx].password;
    existing[idx] = { ...normalized, password: preservedPassword };
  } else {
    existing.push(normalized);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}

/**
 * Refresh a saved account's display info (school/name/role) and
 * last_used timestamp — WITHOUT ever creating a new entry or touching
 * its password. This is the only function portal-mount "keep this
 * account fresh while its owner is using this portal" effects should
 * call (PrincipalLayout/StudentPages/TeacherLayout). saveAccount() is
 * reserved for the two places that actually have a real password on
 * hand — Add Account and a normal login — so a presence-refresh effect
 * can never accidentally create a broken, password-less tile that
 * looks clickable but can never actually switch.
 */
export function refreshAccountPresence(info: {
  username: string;
  school_id: string;
  school_name: string;
  user_full_name: string;
  role: string;
}) {
  const key = normalizeKey(info.username);
  const existing = getSavedAccounts();
  const idx = existing.findIndex(a => normalizeKey(a.username) === key);
  if (idx < 0) return; // no real, password-bearing entry to refresh — do nothing
  existing[idx] = {
    ...existing[idx],
    school_id: info.school_id,
    school_name: info.school_name,
    user_full_name: info.user_full_name,
    role: info.role,
    last_used: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}

export function removeAccount(username: string) {
  const key = normalizeKey(username);
  const existing = getSavedAccounts().filter(a => normalizeKey(a.username) !== key);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}

export function updateLastUsed(username: string) {
  const key = normalizeKey(username);
  const accounts = getSavedAccounts();
  const idx = accounts.findIndex(a => normalizeKey(a.username) === key);
  if (idx >= 0) {
    accounts[idx].last_used = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  }
}

// ─── Debug (respects ?debug=true) ────────────────────────────
function dbg(tag: string, message: string, data?: any) {
  if (new URLSearchParams(window.location.search).has('debug')) {
    console.info(`[${tag}] ${message}`, data ?? '');
  }
}

// ─── AccountSwitcher Component ────────────────────────────────
interface AccountSwitcherProps {
  /** The currently active username so we can highlight it */
  currentUsername: string;
  /** Called when a switch succeeds — parent should re-fetch dashboard */
  onSwitchSuccess?: () => void;
}

export default function AccountSwitcher({ currentUsername, onSwitchSuccess }: AccountSwitcherProps) {
  const [accounts, setAccounts] = useState<SavedAccount[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addUsername, setAddUsername] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const refresh = () => setAccounts(getSavedAccounts());

  useEffect(() => {
    refresh();
  }, []);

  // Keep the shared "busy" flag in sync with whether this component is
  // genuinely in the middle of a credential-entry step — the Add Account
  // modal being open, or a switch attempt in flight. See
  // lib/accountSwitcherState.ts for why this exists: while either of
  // these is true, the CURRENT account's real session is still fully
  // active (nothing here touches it until a target is confirmed valid),
  // so a legitimate notice for that account could otherwise pop up right
  // on top of this password UI. Also resets to false on unmount, in case
  // this component goes away mid-flow without addOpen/switchingId ever
  // naturally clearing (e.g. navigating away).
  useEffect(() => {
    setAccountSwitcherBusy(addOpen || switchingId !== null);
    return () => setAccountSwitcherBusy(false);
  }, [addOpen, switchingId]);

  // ── Add new account ──────────────────────────────────────
  const handleAddAccount = async () => {
    if (!addUsername.trim() || !addPassword.trim()) {
      setAddError('Username and password are required.');
      return;
    }
    setAddLoading(true);
    setAddError('');

    try {
      // Detect if this looks like a phone number (parent) or a username
      const looksLikePhone = /^\+?[\d\s\-]{7,}$/.test(addUsername.trim());

      // Use the isolated validationClient here — NOT the main `supabase`
      // client — so checking these credentials can never disturb the
      // session of whoever is currently logged in.
      const { data, error } = looksLikePhone
        // Parents authenticate via a synthetic email derived from their
        // phone (see AuthContext.signIn's parentEmail) — NOT via
        // Supabase's native `phone` auth field, which these accounts
        // were never set up to use. Using `{ phone }` here always
        // failed regardless of correct credentials.
        ? await validationClient.auth.signInWithPassword({ email: `${addUsername.trim().replace(/\+/g, '')}@edtrack.internal`, password: addPassword })
        : await validationClient.auth.signInWithPassword({ email: `${addUsername.toLowerCase().trim()}@edtrack.internal`, password: addPassword });
      if (error || !data.user) {
        setAddError('Incorrect username or password.');
        setAddLoading(false);
        return;
      }

      // Fetch user profile (via validationClient — same reasoning as above)
      const { data: userProfile } = await validationClient
        .from('users').select('full_name, school_id, role, username').eq('id', data.user.id).single();

      if (!userProfile) {
        setAddError('Account not found. Contact your principal.');
        setAddLoading(false);
        // Nothing to undo — validationClient never touched the real session
        // or localStorage, so the currently logged-in user is untouched.
        return;
      }

      // Fetch school name
      let schoolName = 'Unknown School';
      if (userProfile.school_id) {
        const { data: schoolData } = await validationClient
          .from('schools').select('name').eq('id', userProfile.school_id).single();
        if (schoolData) schoolName = schoolData.name;
      }

      const newAccount: SavedAccount = {
        // Use the identifier that just successfully authenticated, not the DB
        // users.username field. For parents, users.username is a generated
        // username — not their phone number — and switching re-authenticates
        // via a synthetic email built from this identifier (see handleSwitch),
        // so this must stay the phone/username actually typed here.
        // normalizeKey() also guards against case/whitespace drift for
        // non-parent roles.
        username: normalizeKey(addUsername),
        password: addPassword,
        school_id: userProfile.school_id || '',
        school_name: schoolName,
        user_full_name: userProfile.full_name,
        role: userProfile.role,
        last_used: new Date().toISOString(),
      };

      saveAccount(newAccount);
      dbg('PAR_ACCOUNT_ADD', 'New account saved', { username: newAccount.username, school: schoolName });

      // Drop the validationClient's own in-memory session now that we're
      // done with it — it was never persisted, so this is just tidiness,
      // not required for correctness.
      validationClient.auth.signOut().catch(() => {});

      refresh();
      setAddOpen(false);
      setAddUsername('');
      setAddPassword('');

      // Switch to the newly added account — this is the point where we
      // intentionally replace the real session, via the main `supabase`
      // client, followed by a full reload.
      await handleSwitch(newAccount);
    } catch {
      setAddError('An unexpected error occurred.');
    } finally {
      setAddLoading(false);
    }
  };

  // ── Switch to a saved account ────────────────────────────
  const handleSwitch = async (account: SavedAccount) => {
    if (normalizeKey(account.username) === normalizeKey(currentUsername)) return;
    if (!account.password) {
      setSwitchError(`No saved password for ${account.user_full_name} yet — this account was registered automatically. Please add it again with their password to enable switching.`);
      return;
    }
    setSwitchingId(account.username);
    dbg('PAR_ACCOUNT_SWITCH', 'Switching account', { targetUsername: account.username, targetSchool: account.school_id });

    try {
      const isParent = account.role === 'parent';
      const email = isParent
        ? `${account.username.replace(/\+/g, '')}@edtrack.internal`
        : `${account.username.toLowerCase().trim()}@edtrack.internal`;

      // Verify credentials AND restriction status against the isolated
      // validationClient FIRST — same reasoning as handleAddAccount above.
      //
      // This replaces the previous approach, which authenticated directly
      // on the shared `supabase` client and only ran the restriction check
      // afterward. That was the actual bug: signInWithPassword() on the
      // real client replaces the currently logged-in user's session the
      // instant the password succeeds — before the restriction check ever
      // ran. So a blocked switch was still destroying the current session:
      // the SIGNED_IN listener would correctly sign the restricted target
      // back out a moment later, but by then the original account's
      // session was already gone, dropping the user to the login screen
      // instead of leaving their real session untouched.
      //
      // By checking on validationClient first, neither a wrong password
      // nor a restricted target ever touches the real session at all.
      const { data, error } = await validationClient.auth.signInWithPassword({ email, password: account.password });

      if (error || !data.user) {
        dbg('PAR_ACCOUNT_SWITCH_FAIL', 'Auto-login failed — credentials may have changed', { username: account.username });
        // Remove stale account and prompt re-add
        removeAccount(account.username);
        refresh();
        setSwitchError(`Could not switch to ${account.user_full_name}. Their password may have changed. Please add the account again.`);
        setSwitchingId(null);
        return;
      }

      // Pass validationClient explicitly — checkStudentRestriction defaults
      // to the shared `supabase` client, which at this point is STILL the
      // current (pre-switch) account's session, not the target's. Since
      // `data` above came from validationClient's own sign-in, the target
      // account's rows are only guaranteed readable through THAT client
      // (self-access), not necessarily through the current account's
      // session (a different, likely-stricter RLS question). See the
      // comment on checkStudentRestriction's definition in AuthContext.tsx.
      const restrictionError = await checkStudentRestriction(data.user.id, validationClient);

      // Done with validationClient's session either way — it was never
      // persisted, so this is tidiness, not required for correctness.
      validationClient.auth.signOut().catch(() => {});

      if (restrictionError) {
        setSwitchError(restrictionError);
        setSwitchingId(null);
        // The current session was never touched — validationClient did
        // all the checking above, so there is nothing to undo here. The
        // user stays exactly where they were, on their existing account.
        return;
      }

      // Only now — target credentials confirmed valid AND confirmed not
      // restricted — do we touch the real session, via the shared
      // `supabase` client, followed by a full reload. This will also fire
      // AuthContext's SIGNED_IN listener, which reruns checkStudentRestriction
      // as its own backstop; since we've already confirmed it passes, that
      // second run is just a redundant confirmation, not a race that can
      // undo anything here.
      const { error: realSignInError } = await supabase.auth.signInWithPassword({ email, password: account.password });
      if (realSignInError) {
        // Extremely unlikely given validationClient just succeeded with the
        // same credentials moments earlier, but don't leave the UI hanging
        // (or silently do nothing) if it somehow happens anyway.
        dbg('PAR_ACCOUNT_SWITCH_FAIL', 'Real client sign-in failed after validation passed', { username: account.username });
        setSwitchError(`Could not switch to ${account.user_full_name}. Please try again.`);
        setSwitchingId(null);
        return;
      }

      updateLastUsed(account.username);
      dbg('PAR_ACCOUNT_SWITCH', 'Switch succeeded', { username: account.username });

      // Navigate to appropriate dashboard based on role — via the same
      // getRoleBasePath() AuthContext.signIn() uses, instead of a second,
      // separately-maintained map. That local copy had drifted out of sync
      // and was missing 'super_admin' entirely, so switching INTO an admin
      // account silently sent you to /login instead of /admin.
      const path = getRoleBasePath(account.role as UserRole);
      window.location.href = path;
    } catch {
      dbg('PAR_ACCOUNT_SWITCH_FAIL', 'Unexpected error during switch');
      setSwitchingId(null);
    }
  };

  // Sort: active first, then by last_used desc
  const sorted = [...accounts].sort((a, b) => {
    if (normalizeKey(a.username) === normalizeKey(currentUsername)) return -1;
    if (normalizeKey(b.username) === normalizeKey(currentUsername)) return 1;
    return new Date(b.last_used).getTime() - new Date(a.last_used).getTime();
  });

  return (
    <div>
      <p className="text-sm font-bold text-text-primary mb-3">Saved Accounts</p>

      {sorted.length === 0 ? (
        <Card className="bg-surface-2 border-dashed border-2 border-border mb-3">
          <p className="text-xs text-text-tertiary text-center py-2">No other accounts saved yet.</p>
        </Card>
      ) : (
        <div className="space-y-2 mb-3 max-h-72 overflow-y-auto overscroll-contain pr-1">
          {sorted.map(account => {
            const isActive = normalizeKey(account.username) === normalizeKey(currentUsername);
            const isSwitching = switchingId === account.username;
            const initials = account.user_full_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

            return (
              <div
                key={account.username}
                className={clsx(
                  'flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all',
                  !isActive && 'bg-white border-border'
                )}
                style={isActive ? {
                  backgroundColor: 'var(--school-dominant-50, #eff6ff)',
                  borderColor: 'color-mix(in srgb, var(--school-dominant-500, #3b82f6) 35%, white)',
                } : undefined}
              >
                {/* Avatar */}
                <div
                  className={clsx(
                    'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm',
                    isActive ? 'text-white' : 'bg-slate-100 text-slate-600'
                  )}
                  style={isActive ? { backgroundColor: 'var(--school-dominant-500, #3b82f6)' } : undefined}
                >
                  {initials}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0" onClick={() => !isActive && handleSwitch(account)} style={{ cursor: isActive ? 'default' : 'pointer' }}>
                  <p className="text-sm font-semibold text-text-primary truncate">{account.user_full_name}</p>
                  <p className="text-xs text-text-tertiary truncate">{account.role === 'parent' ? 'Parent' : account.school_name}</p>
                  {account.role !== 'parent' && (
                    <p className="text-[10px] text-text-tertiary capitalize">{account.role}</p>
                  )}
                  {!account.password && (
                    <p className="text-[10px] text-amber-600 font-medium">Needs password — tap to re-add</p>
                  )}
                </div>

                {/* Status / action */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isActive ? (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--school-dominant-500, #3b82f6)' }}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  ) : isSwitching ? (
                    <div className="w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: 'var(--school-dominant-500, #3b82f6)', borderTopColor: 'transparent' }} />
                  ) : null}

                  {/* Remove button — only for non-active accounts */}
                  {!isActive && (
                    <button
                      onClick={() => setConfirmRemove(account.username)}
                      className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-400 transition-colors"
                      title="Remove saved account"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Account Button */}
      <button
        onClick={() => { setAddOpen(true); setAddError(''); setAddUsername(''); setAddPassword(''); }}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-border text-sm font-semibold text-text-secondary hover:border-[var(--school-dominant-500,#3b82f6)] hover:text-[var(--school-dominant-600,#2563eb)] transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Another Account
      </button>

      {/* Add Account Modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Account">
        <div className="space-y-4">
          <p className="text-xs text-text-secondary">Enter credentials for the account you want to add. It will be saved for quick switching.</p>
          {addError && <p className="text-xs text-red-500 font-medium">{addError}</p>}
          <Input
            label="Username *"
            placeholder="e.g. t.schoolname.john"
            value={addUsername}
            onChange={e => setAddUsername(e.target.value)}
          />
          <Input
            label="Password *"
            placeholder="Password"
            type="password"
            value={addPassword}
            onChange={e => setAddPassword(e.target.value)}
          />
          <Button
            fullWidth
            loading={addLoading}
            onClick={handleAddAccount}
            className="text-white border-0"
            style={{ background: 'linear-gradient(135deg, var(--school-dominant-500, #3b82f6), var(--school-dominant-700, #1d4ed8))' }}
          >
            Add & Switch
          </Button>
        </div>
      </Modal>

      {/* Confirm Remove Modal */}
      <Modal open={!!confirmRemove} onClose={() => setConfirmRemove(null)} title="Remove Account">
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Remove <strong>{accounts.find(a => a.username === confirmRemove)?.user_full_name}</strong> from saved accounts? You can add it again anytime.
          </p>
          <div className="flex gap-3">
            <Button fullWidth variant="ghost" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <button
              onClick={() => {
                if (confirmRemove) { removeAccount(confirmRemove); refresh(); }
                setConfirmRemove(null);
              }}
              className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      </Modal>

      {/* Switch Failed Notice */}
      <Modal open={!!switchError} onClose={() => setSwitchError(null)} title="Couldn't Switch">
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">{switchError}</p>
          <Button
            fullWidth
            className="text-white border-0"
            style={{ background: 'linear-gradient(135deg, var(--school-dominant-500, #3b82f6), var(--school-dominant-700, #1d4ed8))' }}
            onClick={() => setSwitchError(null)}
          >
            Got it
          </Button>
        </div>
      </Modal>
    </div>
  );
}
